import { ExportIcon } from "@xola/icons";
import { ButtonGroup, Tooltip } from "@xola/ui-kit";
import { isFuture, JsDateRange, now, PurchaseItemStatus } from "common";
import React, { useState } from "react";
import { ExportJob, GuestStatus } from "types";
import shallow from "zustand/shallow";
import { useResource } from "../../../hooks/useResource";
import { useModal } from "../../../hooks/useVisibility";
import { getInitialDate } from "../../../models/Reports";
import { AnalyticsMode } from "../../../stores/reports/analyticsFilterStore";
import { useCustomerAnalyticsFilterStore } from "../../../stores/reports/customerAnalyticsFilterStore";
import { ExportJobModal } from "../../Common/ExportJobModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ListFilter } from "../../Dashboard/Filters/ListFilter";
import { ItemSummary } from "../AnalyticsProductPicker";
import { ReportDatePicker } from "../ReportDatePicker";

export const CustomerAnalyticsFilter = () => {
    const exportModal = useModal(false);

    const [from, to] = useCustomerAnalyticsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const [setFrom, setTo] = useCustomerAnalyticsFilterStore((state) => [state.setFrom, state.setTo], shallow);
    const [filters, setFilters] = useCustomerAnalyticsFilterStore(
        (state) => [state.filters, state.setFilters],
        shallow,
    );
    const isCashFlow = filters.mode === AnalyticsMode.CASH_FLOW;

    const [value, setValue] = useState(getInitialDate(from, to));
    const handleDateChange = (dateValue: JsDateRange) => {
        if (dateValue.from && dateValue.to) {
            setFrom(now(dateValue.from));
            setTo(now(dateValue.to));
        }

        setValue(dateValue);
    };

    const activeMode = filters.mode === AnalyticsMode.ARRIVAL_DATE ? 0 : 1;
    const handleModeChange = (activeIndex: number) => {
        const mode = activeIndex === 0 ? AnalyticsMode.ARRIVAL_DATE : AnalyticsMode.CASH_FLOW;
        setFilters({ mode });
    };

    return (
        <div className="flex flex-row items-center justify-between border-b border-gray-light px-6 py-3">
            <div className="flex w-full flex-row items-center justify-start space-x-1 xl:space-x-4">
                <div>
                    <ReportDatePicker
                        value={value}
                        from={from}
                        to={to}
                        max={isCashFlow ? now() : null}
                        disabledDays={(date) => isCashFlow && isFuture(date, "day")}
                        isFutureDatesAllowed={filters.mode === AnalyticsMode.ARRIVAL_DATE}
                        onChange={handleDateChange}
                    />
                </div>
                <div>
                    <ListFilter
                        label="Product"
                        title="Filter Products"
                        collection={["experiences"]}
                        target={ItemSummary}
                        value={filters.products}
                        onChange={(products) => setFilters({ products })}
                    />
                </div>
                <div>
                    <ButtonGroup size="medium" value={activeMode} onChange={handleModeChange}>
                        <ButtonGroup.Button>
                            <Tooltip
                                content={
                                    <span>
                                        Date of event shows data based on the arrival date of the reservation. Data
                                        excludes guest count from reservations that are cancelled, declined or pending.
                                    </span>
                                }
                            >
                                Date Of Event
                            </Tooltip>
                        </ButtonGroup.Button>
                        <ButtonGroup.Button>
                            <Tooltip
                                content={
                                    <span>
                                        Date of purchase shows data based on the purchase creation date. Data excludes
                                        guest count from reservations that are cancelled, declined or pending.
                                    </span>
                                }
                            >
                                Date Of Purchase
                            </Tooltip>
                        </ButtonGroup.Button>
                    </ButtonGroup>
                </div>
            </div>
            <div className="shrink-0 space-x-2 place-self-end pl-2">
                <SecondaryButton icon={<ExportIcon />} onClick={exportModal.show}>
                    Export
                </SecondaryButton>
            </div>
            <ExportCustomerModal isOpen={exportModal.isVisible} onClose={exportModal.hide} />
        </div>
    );
};

const ExportCustomerModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: (e: any) => void }) => {
    const filters = useCustomerAnalyticsFilterStore((state) => state.filters);
    const dateParams = useCustomerAnalyticsFilterStore((state) => state.getDateParams());

    const params: any = {
        cover: false,
        status: PurchaseItemStatus.CONFIRMED,
        "guestStatus[notEqual]": GuestStatus.NO_SHOW,
        ...dateParams,
    };
    if (filters.products.selected.length > 0) {
        params["product[in]"] = filters.products.selected.join(",");
    }

    const { data: job } = useResource<ExportJob>(["analytics", "export", "trips"], params, {
        enabled: isOpen,
        staleTime: 1000,
    });
    return <ExportJobModal isOpen={isOpen} job={job} onClose={onClose} />;
};
